import { useCallback, useEffect, useRef, useState } from "react"
import { useElements, useStripe } from "@stripe/react-stripe-js"
import { captureMessage } from "@sentry/react"

import { post } from "shared/lib/api"
import { rejectedXHRPromiseLogger } from "shared/lib/xhr_promise"

import classNames from "classnames"
import InlineErrors from "shared/components/inline_errors"
import Tooltip from "shared/components/tooltip"
import { gtag } from "shared/lib/utils"

import CreditCardField from "shared/components/credit_card_field"
import SubscriptionHelper from "./subscription_helper"
import PromoCodeForm from "./promo_code_form.js"

import scheduleAbandonedCheckoutEmail from "./abandoned_checkout_mail_helper"

import googlePayLogo from "images/shared/google_pay_logo.svg"
import applePayLogo from "images/shared/apple_pay_logo.svg"

import "./payment_info_form.scss"

interface PlanType {
  name: string
  amount: number
  interval: "month" | "year"
  local_price_formatted: string
  local_price_formatted_monthly: string
  local_price_monthly: number
  currency: string
}

interface Props {
  browserType: string
  createStripeCustomerUrl: string
  eligibleForTrial: boolean
  email: string
  initialPromoCode: string
  monthlyPlan: PlanType
  onModalHandleSuccessfulSubscription: () => void
  planId: string
  productId: string
  redirectToMobileAppOnSuccess: boolean
  referrerPath: string
  renderingInsideModal: boolean
  subscriptionsPath: string
  subscriptionSuccessPath: string
  tier: string
  yearlyPlan: PlanType
}

export default function PaymentInfoForm({
  browserType,
  createStripeCustomerUrl,
  eligibleForTrial,
  email,
  initialPromoCode,
  modalButtonType,
  monthlyPlan,
  onModalHandleSuccessfulSubscription,
  planId,
  productId,
  redirectToMobileAppOnSuccess,
  referrerPath,
  renderingInsideModal,
  subscriptionsPath,
  subscriptionSuccessPath,
  tier,
  yearlyPlan,
}: Props) {
  const isSafari = browserType === "safari"
  const isChrome = browserType === "chrome"

  const stripe = useStripe()
  const elements = useElements()
  const [paymentRequest, setPaymentRequest] = useState(null)

  const cardRef = useRef(null)
  const formRef = useRef(null)
  const [handlingSubmit, setHandlingSubmit] = useState(false)
  const [cardReady, setCardReady] = useState(false)
  const [promoCode, setPromoCode] = useState(initialPromoCode)
  const [plan, setPlan] = useState(yearlyPlan)

  const planName = "Pro"
  const priceTypeMonthly = "monthly"
  const priceTypeYearly = "yearly"
  const [selectedPrice, setSelectedPrice] = useState(yearlyPlan.amount)
  const [validationErrors, setValidationErrors] = useState(null)
  const discountForYearly = Math.round(((monthlyPlan.amount - yearlyPlan.amount / 12) / monthlyPlan.amount) * 100)

  const inlineErrorFields = ["price"]
  const defaultErrorMessagesForField = {
    price: ["Choose a price between $1.99 USD and $100 USD"],
  }

  useEffect(() => {
    // schedule the abandoned checkout worker once the page is viewed since
    // it can only be sent once, to non-pros
    scheduleAbandonedCheckoutEmail(tier)

    post(createStripeCustomerUrl)
      .then(data => {
        const { errors } = data
        if (errors && errors.length > 0 && window.App.allowDebugErrorMessages) {
          captureMessage("createStripeCustomer failed", { extra: errors })
        }
      })
      .catch(rejectedXHRPromiseLogger())
  }, [])

  useEffect(() => {
    const price = getPrice()
    if (stripe && !paymentRequest) {
      const pr = stripe.paymentRequest({
        country: "US",
        currency: price?.currency?.toLowerCase() || "usd",
        total: {
          label: planName,
          amount: selectedPrice,
          pending: true,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      })

      pr.canMakePayment().then(result => {
        if (result) {
          setPaymentRequest(pr)
        }
      })
    } else {
      console.warn(`paymentRequest skipped. Params would be:`)
      console.warn({
        country: "US",
        currency: price?.currency?.toLowerCase() || "usd",
        total: {
          label: planName,
          amount: selectedPrice,
          pending: true,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      })
    }
  }, [stripe, paymentRequest])

  const setupPaymentMethodHandler = useCallback(
    (price: object) => {
      if (!paymentRequest) return

      paymentRequest.on("cancel", async () => setHandlingSubmit(false))

      // this creates a closure, so make sure to do it when the price is fixed
      paymentRequest.on("paymentmethod", async ev => {
        if (handlingSubmit) {
          return
        }
        setHandlingSubmit(true)

        await SubscriptionHelper({
          stripe,
          subscriptionsPath,
          price,
        }).subscribe(
          planId,
          productId,
          ev.paymentMethod,
          promoCode,
          (subscription: object) => {
            ev.complete("success")
            handleSuccessfulSubscription(subscription, planId, promoCode)
          },
          (error: object) => {
            ev.complete("fail")
            setPaymentRequest(null)
            handleError(error)
          }
        )
      })
    },
    [paymentRequest]
  )

  useEffect(() => {
    if (referrerPath && !referrerPath.match(/(user\/new)|(onboarding)/)) {
      sessionStorage.setItem("afterProPaymentPath", referrerPath)
    }
  }, [])

  function handlePaymentRequestButtonClick(event) {
    event.preventDefault()
    if (handlingSubmit) {
      return
    }
    setHandlingSubmit(true)

    const price = getPrice()

    paymentRequest?.update({
      total: {
        label: planName,
        amount: eligibleForTrial ? 0 : price.price_in_cents,
        currency: price.currency || "usd",
      },
    })
    paymentRequest?.show()
    setupPaymentMethodHandler(price)
  }

  async function handleSubmit(event) {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault()

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded. Make sure to disable
      // form submission until Stripe.js has loaded.
      return
    }

    if (handlingSubmit) {
      return
    }
    setHandlingSubmit(true)

    await SubscriptionHelper({
      stripe,
      subscriptionsPath,
      price: getPrice(),
    }).subscribe(
      planId,
      productId,
      {
        card: elements.getElement(cardRef.current.getElement()),
        billing_details: { email },
      },
      promoCode,
      handleSuccessfulSubscription,
      handleError
    )
  }

  function onPageHandleSuccessfulSubscription(subscription: object) {
    let successUrl = subscriptionSuccessPath + subscription.id
    if (redirectToMobileAppOnSuccess) {
      successUrl += "?mobile=1"
    }

    window.location.assign(successUrl)
  }

  function handleSuccessfulSubscription(subscription: object, planId: string, promoCode: string | null) {
    try {
      gtag("event", "registration_steps", {
        step: "payment_complete",
        item_name: planId,
        item_variant: subscription.plan.interval,
        eligible_for_trial: eligibleForTrial,
      })
      // GA4 Purchase event
      gtag("event", "purchase", {
        transaction_id: subscription.id,
        value: subscription.plan.amount,
        currency: subscription.plan.currency,
        coupon: promoCode,
        items: [
          {
            item_id: subscription.plan.product,
            item_name: planId,
            item_variant: subscription.plan.interval,
            eligible_for_trial: eligibleForTrial,
            quantity: 1,
          },
        ],
      })
    } catch (e) {
      if (window.App.env && window.App.allowDebugErrorMessages) console.error(e)
    } finally {
      renderingInsideModal ? onModalHandleSuccessfulSubscription() : onPageHandleSuccessfulSubscription(subscription)
    }
  }

  function handleError(error) {
    setHandlingSubmit(false)
    const errorMessage = error.message || error

    if (!inlineErrorFields.includes(error.attribute)) {
      window.appendFlash(errorMessage, "danger")
      return
    }

    const errors = {}
    const defaultMessages = defaultErrorMessagesForField[error.attribute]
    errors[error.attribute] = defaultMessages || [errorMessage]
    setValidationErrors(errors)
    InlineErrors.scrollWithDelay()
  }

  function handlePriceTypeChange(priceType: string) {
    const plan = priceType === priceTypeYearly ? yearlyPlan : monthlyPlan
    setValidationErrors(null)
    setPlan(plan)
    setSelectedPrice(plan.amount)
  }

  function openZendesk(e) {
    e.preventDefault()
    zE("webWidget", "open")
  }

  function getPrice() {
    return {
      price_type: selectedPrice === yearlyPlan.amount ? priceTypeYearly : priceTypeMonthly,
      price_in_cents: selectedPrice,
      currency: plan.currency,
    }
  }

  function submitButtonDisabled() {
    const formReady = cardReady && !handlingSubmit && !validationErrors
    return !formReady
  }

  const buttonTitle = eligibleForTrial ? "Try it free" : "Upgrade"

  const submittingCopy = eligibleForTrial ? "Submitting..." : "Upgrading..."

  return (
    <form ref={formRef} styleName="payment-info-form" className="form payment-info-form" onSubmit={handleSubmit}>
      <div styleName="stripe-payment-container">
        <section styleName="price-container">
          <div styleName="price-list">
            <div
              styleName={classNames({
                "selected-price": plan.interval === "year",
              })}
              role="button"
              onClick={() => handlePriceTypeChange(priceTypeYearly)}
            >
              <span styleName="price-value">{yearlyPlan.local_price_formatted_monthly}</span>
              <span styleName="price-period">/month</span>
              <span styleName="price-discount">SAVE {discountForYearly}%</span>
            </div>
            <div
              styleName={classNames({
                "selected-price": plan.interval === "month",
              })}
              role="button"
              onClick={() => handlePriceTypeChange(priceTypeMonthly)}
            >
              <span styleName="price-value">{monthlyPlan.local_price_formatted}</span>
              <span styleName="price-period">/month</span>
            </div>
          </div>
          <div styleName="price-interval" className="mt1">
            <div
              styleName={classNames({
                "selected-interval": plan.interval === "year",
              })}
            >
              {`${yearlyPlan.local_price_formatted} billed annually`}
            </div>
            <div
              styleName={classNames({
                "selected-interval": plan.interval === "month",
              })}
            >
              Billed monthly
            </div>
          </div>
        </section>
        <section className="mt4">
          {paymentRequest && (
            <>
              <div className="payment-request-container">
                <button onClick={handlePaymentRequestButtonClick} className="payment-request">
                  {isSafari && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: applePayLogo,
                      }}
                    />
                  )}
                  {isChrome && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: googlePayLogo,
                      }}
                    />
                  )}
                  {!(isSafari || isChrome) && "Pay Now"}
                </button>
              </div>
              <div className="payment-request-separator">
                <div className="rule" />
                <div className="copy">or pay with card</div>
                <div className="rule" />
              </div>
            </>
          )}

          <CreditCardField ref={cardRef} onCardReadyChange={setCardReady} />

          <PromoCodeForm onValidCodeApplied={setPromoCode} initialPromoCode={initialPromoCode} />
        </section>
      </div>
      <section styleName="action-buttons" className="mt4 mt2--phone">
        <button
          id="submit-payment"
          type="submit"
          disabled={handlingSubmit}
          className={modalButtonType ? "button-secondary" : "button-primary full-width"}
          title={buttonTitle}
          aria-label={buttonTitle}
        >
          {handlingSubmit ? submittingCopy : buttonTitle}
          {submitButtonDisabled() && !handlingSubmit && (
            <Tooltip>
              Please be sure you’ve correctly completed the form. <br />
              <a className="link-inline" onClick={openZendesk}>
                Contact us
              </a>
              for help.
            </Tooltip>
          )}
        </button>
      </section>
    </form>
  )
}
